import { z } from 'zod';
import type { UtilisateurResumeDTO } from './dto.js';

/**
 * Assistant IA : vocabulaire, format du script d'appel, offre commerciale et
 * DTO des routes IA (conception 4, 5.4 et 6). Partage par l'API (validation de
 * la sortie du modele, routes) et l'interface (affichage, formulaires).
 */

/* -------------------------------------------------------------------------- */
/*                              Statut et etape                                */
/* -------------------------------------------------------------------------- */

export const STATUTS_SCRIPT = ['EN_ATTENTE', 'EN_COURS', 'PRET', 'ECHEC'] as const;
export type StatutScript = (typeof STATUTS_SCRIPT)[number];

export const LIBELLES_STATUT_SCRIPT: Record<StatutScript, string> = {
  EN_ATTENTE: 'En attente',
  EN_COURS: 'En cours',
  PRET: 'Prêt',
  ECHEC: 'Échec',
};

export const ETAPES_SCRIPT = ['EN_FILE', 'LECTURE_SITE', 'REDACTION', 'TERMINE'] as const;
export type EtapeScript = (typeof ETAPES_SCRIPT)[number];

export const LIBELLES_ETAPE_SCRIPT: Record<EtapeScript, string> = {
  EN_FILE: "En file d'attente",
  LECTURE_SITE: 'Lecture du site',
  REDACTION: 'Rédaction du script',
  TERMINE: 'Terminé',
};

/* -------------------------------------------------------------------------- */
/*                       Format du script (sortie du modele)                   */
/* -------------------------------------------------------------------------- */

export const NATURES_SIGNAL = ['opportunite', 'frein', 'neutre'] as const;
export type NatureSignal = (typeof NATURES_SIGNAL)[number];

export const LIBELLES_NATURE_SIGNAL: Record<NatureSignal, string> = {
  opportunite: 'Opportunité',
  frein: 'Frein',
  neutre: 'Neutre',
};

/**
 * Bornes « dures » : elles ecartent une reponse cassee (texte vide, liste vide,
 * volume absurde). Les cibles editoriales (3 a 6 points cles, accroche de moins
 * de 25 secondes...) sont demandees dans les consignes : les imposer ici ferait
 * echouer, donc facturer pour rien, une reponse simplement un peu longue. Elles
 * ne partent pas dans le JSON Schema envoye au modele (conception 6.4).
 */
const texteScript = z.string().trim().min(1, 'Texte vide').max(2000, 'Texte trop long');
const MAX_ELEMENTS = 8;

export const signalScriptSchema = z.object({
  libelle: texteScript,
  nature: z.enum(NATURES_SIGNAL),
});

export const objectionScriptSchema = z.object({
  objection: texteScript,
  reponse: texteScript,
});

/**
 * Bornes de `pagesConsultees`. Le client IA recale cette liste sur les pages
 * reellement lues et la borne de meme : un script `PRET` doit toujours se
 * relire sans perdre son analyse.
 */
export const MAX_PAGES_CONSULTEES = 10;
export const LONGUEUR_MAX_URL_PAGE = 2000;

export const analyseScriptSchema = z.object({
  siteAnalyse: z.boolean(),
  resume: texteScript,
  pointsCles: z.array(texteScript).min(1).max(MAX_ELEMENTS),
  signaux: z.array(signalScriptSchema).max(MAX_ELEMENTS),
  angle: texteScript,
  pagesConsultees: z.array(z.string().trim().max(LONGUEUR_MAX_URL_PAGE)).max(MAX_PAGES_CONSULTEES),
});

export const contenuScriptSchema = z.object({
  accroche: texteScript,
  decouverte: z.array(texteScript).min(1).max(MAX_ELEMENTS),
  argumentaire: z.array(texteScript).min(1).max(MAX_ELEMENTS),
  objections: z.array(objectionScriptSchema).max(MAX_ELEMENTS),
  conclusion: texteScript,
  messageRepondeur: texteScript,
});

/** Reponse attendue du modele : l'analyse du prospect puis le script. */
export const scriptGenereSchema = z.object({
  analyse: analyseScriptSchema,
  contenu: contenuScriptSchema,
});

export type SignalScript = z.infer<typeof signalScriptSchema>;
export type ObjectionScript = z.infer<typeof objectionScriptSchema>;
export type AnalyseScript = z.infer<typeof analyseScriptSchema>;
export type ContenuScript = z.infer<typeof contenuScriptSchema>;
export type ScriptGenere = z.infer<typeof scriptGenereSchema>;

/* -------------------------------------------------------------------------- */
/*                             Demande de generation                           */
/* -------------------------------------------------------------------------- */

export const LONGUEUR_MAX_CONSIGNE = 300;

export const genererScriptSchema = z.object({
  /** Consigne de regeneration (« plus court », « ton plus direct »...). */
  consigne: z
    .string()
    .trim()
    .max(LONGUEUR_MAX_CONSIGNE, `La consigne ne doit pas dépasser ${LONGUEUR_MAX_CONSIGNE} caractères.`)
    .optional(),
});
export type GenererScript = z.infer<typeof genererScriptSchema>;

/* -------------------------------------------------------------------------- */
/*                         Offre commerciale (« Votre offre »)                 */
/* -------------------------------------------------------------------------- */

const champOffre = (max: number) => z.string().trim().max(max, `${max} caractères au maximum.`);

/** Fiche remplacee en entier a chaque enregistrement (`PUT`). */
export const majOffreSchema = z.object({
  entreprise: champOffre(120),
  offre: champOffre(2000),
  cible: champOffre(1000),
  benefices: champOffre(2000),
  preuves: champOffre(2000),
  objectifAppel: champOffre(300),
  objections: champOffre(3000),
  ton: champOffre(300),
});
export type MajOffre = z.infer<typeof majOffreSchema>;

export const CHAMPS_OFFRE = [
  'entreprise',
  'offre',
  'cible',
  'benefices',
  'preuves',
  'objectifAppel',
  'objections',
  'ton',
] as const satisfies ReadonlyArray<keyof MajOffre>;

export const LIBELLES_CHAMPS_OFFRE: Record<keyof MajOffre, string> = {
  entreprise: 'Votre entreprise',
  offre: 'Ce que vous vendez',
  cible: 'À qui',
  benefices: 'Bénéfices clés (un par ligne)',
  preuves: 'Preuves : clients, chiffres, références',
  objectifAppel: "Objectif de l'appel",
  objections: 'Objections fréquentes et réponses',
  ton: 'Ton',
};

export const OFFRE_VIDE: MajOffre = {
  entreprise: '',
  offre: '',
  cible: '',
  benefices: '',
  preuves: '',
  objectifAppel: '',
  objections: '',
  ton: '',
};

/** L'essentiel de la fiche : ce que l'entreprise vend. Sans lui, l'IA reste generique. */
export function offreEstRenseignee(offre: Pick<MajOffre, 'offre'>): boolean {
  return offre.offre.trim().length > 0;
}

/* -------------------------------------------------------------------------- */
/*                                Consommation                                 */
/* -------------------------------------------------------------------------- */

export const requeteConsommationIaSchema = z.object({
  mois: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Mois attendu au format AAAA-MM.')
    .optional(),
});
export type RequeteConsommationIa = z.infer<typeof requeteConsommationIaSchema>;

/* -------------------------------------------------------------------------- */
/*                                     DTO                                     */
/* -------------------------------------------------------------------------- */

export interface ScriptAppelDTO {
  id: string;
  leadId: string;
  statut: StatutScript;
  etape: EtapeScript;
  /** Detail de l'etape : l'URL en cours de lecture. */
  detailEtape: string | null;
  consigne: string | null;
  /** Site officiel lu (https), `null` si aucun site exploitable. */
  urlSite: string | null;
  /** `null` tant que le script n'est pas pret. */
  analyse: AnalyseScript | null;
  contenu: ContenuScript | null;
  modele: string | null;
  /** Tokens d'entree, cache compris. */
  tokensEntree: number | null;
  tokensSortie: number | null;
  /** Estimation en dollars ; `null` pour un modele sans tarif connu. */
  coutEstimeUsd: number | null;
  /** Message lisible en cas d'echec. */
  erreur: string | null;
  demandePar: UtilisateurResumeDTO | null;
  creeLe: string;
  termineLe: string | null;
}

export interface ReponseScriptDTO {
  script: ScriptAppelDTO;
}

export interface OffreCommercialeDTO extends MajOffre {
  /** `null` tant que la fiche n'a jamais ete enregistree. */
  majLe: string | null;
  majPar: UtilisateurResumeDTO | null;
}

export interface EtatIaDTO {
  /** Cle API presente : sans elle, toute generation repond 503 `IA_NON_CONFIGUREE`. */
  configure: boolean;
  modele: string;
  quotaJour: number;
  /**
   * Generations demandees depuis minuit, heure de Paris, dont les echecs
   * factures (tokens consommes) ; un echec sans reponse n'y compte pas. Ecart
   * voulu a la conception 6.5 (echecs exclus) : le plafond borne aussi le
   * budget et les lectures de site.
   */
  utilisesAujourdhui: number;
  /** La fiche offre dit ce que vend l'entreprise (sinon, inviter l'administrateur a la remplir). */
  offreRenseignee: boolean;
}

export interface LigneConsommationIaDTO {
  /** Generations demandees, echecs compris. */
  scripts: number;
  echecs: number;
  tokensEntree: number;
  tokensSortie: number;
  /** Somme des couts estimes ; un modele sans tarif compte pour zero. */
  coutEstimeUsd: number;
}

export interface ConsommationIaJourDTO extends LigneConsommationIaDTO {
  /** Jour civil de Paris, `AAAA-MM-JJ`. */
  jour: string;
}

export interface ConsommationIaUtilisateurDTO extends LigneConsommationIaDTO {
  utilisateur: UtilisateurResumeDTO | null;
}

export interface ConsommationIaDTO {
  /** `AAAA-MM`, heure de Paris. */
  mois: string;
  total: LigneConsommationIaDTO;
  parJour: ConsommationIaJourDTO[];
  parUtilisateur: ConsommationIaUtilisateurDTO[];
}

/* -------------------------------------------------------------------------- */
/*                         Emplacements et mention RGPD                        */
/* -------------------------------------------------------------------------- */

/**
 * Le script ne nomme personne : il ecrit `{{contact}}` et `{{commercial}}`, que
 * l'interface remplace a l'affichage. Le nom du dirigeant n'est ainsi jamais
 * transmis a l'IA (conception 6.2).
 */
export const EMPLACEMENT_CONTACT = '{{contact}}';
export const EMPLACEMENT_COMMERCIAL = '{{commercial}}';
export const CONTACT_PAR_DEFAUT = 'Madame, Monsieur';

/** Phrase fixe affichee sous chaque script (conception 6.4 et 9). */
export const MENTION_OPPOSITION =
  'Si vous ne souhaitez plus être contacté, dites-le-moi et je le note tout de suite.';

export interface ValeursEmplacements {
  /** Prenom et nom du dirigeant principal ; `null` : « Madame, Monsieur ». */
  contact: string | null;
  /** Nom de l'utilisateur connecte. */
  commercial: string;
}

export function remplacerEmplacements(texte: string, valeurs: ValeursEmplacements): string {
  const contact = valeurs.contact?.trim() || CONTACT_PAR_DEFAUT;
  // Remplacement par fonction : un `$` dans une valeur n'est jamais interprete.
  return texte.replace(/\{\{\s*(contact|commercial)\s*\}\}/gi, (_tout, nom: string) =>
    nom.toLowerCase() === 'contact' ? contact : valeurs.commercial,
  );
}

export function personnaliserContenu(contenu: ContenuScript, valeurs: ValeursEmplacements): ContenuScript {
  const remplacer = (texte: string): string => remplacerEmplacements(texte, valeurs);
  return {
    accroche: remplacer(contenu.accroche),
    decouverte: contenu.decouverte.map(remplacer),
    argumentaire: contenu.argumentaire.map(remplacer),
    objections: contenu.objections.map((ligne) => ({
      objection: remplacer(ligne.objection),
      reponse: remplacer(ligne.reponse),
    })),
    conclusion: remplacer(contenu.conclusion),
    messageRepondeur: remplacer(contenu.messageRepondeur),
  };
}
